import { revealShopifyCollection } from './skeletons';
import { loadShopifyCollectionModules } from './shopify';

const FALLBACK_TARGET_SELECTOR =
  '[data-constructor-shopify-fallback-target]';
const FALLBACK_URL_ATTRIBUTE = 'data-constructor-shopify-fallback-url';
const FALLBACK_RESPONSE_SELECTOR =
  '.template-collection__product-list__wrapper';
const FALLBACK_FILTERS_SELECTOR = '[data-filter-groups]';
const FALLBACK_ACTIVE_FILTERS_SELECTOR = '[data-shopify-active-facets]';
const pendingFallbacks = new WeakMap();

const getWindow = (element) =>
  element?.ownerDocument?.defaultView ||
  (typeof window !== 'undefined' ? window : null);

/** Builds the existing lightweight Shopify collection endpoint URL. */
export const getShopifyCollectionFallbackUrl = (element) => {
  const currentWindow = getWindow(element);
  const fallbackPath = element?.getAttribute?.(FALLBACK_URL_ATTRIBUTE);

  if (!currentWindow?.location || !fallbackPath) {
    return '';
  }

  const url = new URL(fallbackPath, currentWindow.location.origin);
  const currentParams = new URLSearchParams(currentWindow.location.search);
  const products = element.querySelector?.('[data-collection-products]');

  currentParams.forEach((value, key) => {
    if (
      key !== 'view' &&
      key !== 'section_id' &&
      !key.startsWith('constructor_filter.')
    ) {
      url.searchParams.append(key, value);
    }
  });

  url.searchParams.set('view', 'ajax');
  url.searchParams.set('constructor_fallback', '1');

  if (products?.dataset?.limit) {
    url.searchParams.set('limit', products.dataset.limit);
  }

  return `${url.pathname}${url.search}`;
};

const getFallbackRoot = (element) =>
  element?.closest?.('[data-collection-section]') || element;

/**
 * A slow Shopify response must not overwrite a Constructor render.
 *
 * An empty Constructor response also finishes the generic widget controller
 * in the `loaded` state, but `revealShopifyCollection()` marks that collection
 * as fallen back. Shopify section responses keep rendering the lightweight
 * Constructor stand-in, so the fallback must remain repeatable after every
 * filter or pagination update while that marker is present.
 */
export const isConstructorCollectionLoaded = (element) => {
  const root = getFallbackRoot(element);

  return (
    root?.dataset?.constructorWidgetState === 'loaded' &&
    !root.hasAttribute?.('data-constructor-collection-fallback')
  );
};

const importFallbackMarkup = (element, html) => {
  const document = element?.ownerDocument;
  const currentWindow = getWindow(element);
  const target = element?.querySelector?.(FALLBACK_TARGET_SELECTOR);

  if (!document || !target || !currentWindow?.DOMParser) {
    return false;
  }

  const responseDocument = new currentWindow.DOMParser().parseFromString(
    html,
    'text/html'
  );
  const source = responseDocument.querySelector(FALLBACK_RESPONSE_SELECTOR);
  const sourceFilters = responseDocument.querySelector(
    FALLBACK_FILTERS_SELECTOR
  );
  const targetFilters = getFallbackRoot(element).querySelector(
    FALLBACK_FILTERS_SELECTOR
  );
  const sourceActiveFilters = responseDocument.querySelector(
    FALLBACK_ACTIVE_FILTERS_SELECTOR
  );
  const targetActiveFilters = getFallbackRoot(element).querySelector(
    FALLBACK_ACTIVE_FILTERS_SELECTOR
  );

  if (
    !source ||
    !sourceFilters ||
    !targetFilters ||
    !sourceActiveFilters ||
    !targetActiveFilters
  ) {
    return false;
  }

  const count = source.querySelector(
    '[data-collection-products-list]'
  )?.dataset?.productsCount;
  const fragment = document.createDocumentFragment();

  Array.from(source.childNodes).forEach((node) => {
    fragment.appendChild(document.importNode(node, true));
  });

  const filtersFragment = document.createDocumentFragment();

  Array.from(sourceFilters.childNodes).forEach((node) => {
    filtersFragment.appendChild(document.importNode(node, true));
  });

  const activeFiltersFragment = document.createDocumentFragment();

  Array.from(sourceActiveFilters.childNodes).forEach((node) => {
    activeFiltersFragment.appendChild(document.importNode(node, true));
  });

  targetActiveFilters.replaceChildren(activeFiltersFragment);
  targetFilters.replaceChildren(filtersFragment);
  target.replaceWith(fragment);

  if (count !== undefined) {
    const section = element.closest('[data-collection-section]') || document;
    const countElement = section.querySelector(
      '[data-collection-product-count]'
    );

    if (countElement) {
      countElement.textContent = `(${count})`;
    }
  }

  return true;
};

const runFallbackCallbacks = (element, reason) => {
  const currentWindow = getWindow(element);
  const runCallbacks = (callbacks) => {
    callbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('Shopify collection fallback callback error', error);
      }
    });
  };

  currentWindow.callbacks_filters_after_render_products =
    currentWindow.callbacks_filters_after_render_products || [];
  runCallbacks(currentWindow.callbacks_filters_after_render_products);

  currentWindow.callbacks_pagination_after_change =
    currentWindow.callbacks_pagination_after_change || [];
  runCallbacks(currentWindow.callbacks_pagination_after_change);

  runCallbacks([
    () => currentWindow?.LazyLoaderRun?.(),
    () => currentWindow?.dy_productCardPerformance?.(),
  ]);
  if (typeof currentWindow?.CustomEvent === 'function') {
    element?.dispatchEvent?.(
      new currentWindow.CustomEvent('constructorio:collection:fallback', {
        bubbles: true,
        detail: { element, reason, type: 'collection' },
      })
    );
  }
};

/**
 * Hands a priority collection back to Shopify without putting complete
 * product-card or filter markup in the initial HTML.
 *
 * A/B collections still carry their complete Liquid grid and have no fallback
 * URL, so they retain the old immediate reveal. A forced Constructor
 * collection carries only lightweight product and filter placeholders; its
 * `?view=ajax&constructor_fallback=1` response is fetched once, and its full
 * Shopify grid and facet groups are transplanted before the collection becomes
 * interactive as Shopify-owned. The normal collection
 * callbacks run after the transplant so lazy images, inline banners and
 * pagination behave exactly as they do after a Shopify filter response.
 */
export const loadShopifyCollectionFallback = (
  element,
  reason = 'error'
) => {
  const root = getFallbackRoot(element);

  if (!root?.querySelector) {
    return Promise.resolve(false);
  }

  if (isConstructorCollectionLoaded(root)) {
    return Promise.resolve(false);
  }

  const target = root.querySelector(FALLBACK_TARGET_SELECTOR);
  const url = getShopifyCollectionFallbackUrl(root);
  const collectionModulesReady = loadShopifyCollectionModules(root).catch(
    (error) => {
      console.error('Shopify collection controller error', error);
      return false;
    }
  );
  const pending = pendingFallbacks.get(root);

  if (pending) {
    return pending;
  }

  if (!target || !url) {
    root.setAttribute('data-constructor-collection-fallback-loading', '');
    revealShopifyCollection(root, reason);
    const request = collectionModulesReady
      .then(() => {
        runFallbackCallbacks(root, reason);
        return false;
      })
      .finally(() => {
        root.removeAttribute('data-constructor-collection-fallback-loading');
        pendingFallbacks.delete(root);
      });

    pendingFallbacks.set(root, request);
    return request;
  }

  root.setAttribute('data-constructor-collection-fallback-loading', '');

  const currentWindow = getWindow(root);
  if (typeof currentWindow?.fetch !== 'function') {
    revealShopifyCollection(root, reason);
    root.removeAttribute('data-constructor-collection-fallback-loading');
    return Promise.resolve(false);
  }

  const request = currentWindow
    .fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
    .then((response) => {
      if (!response.ok) {
        throw new Error(
          `Shopify collection fallback failed (${response.status}).`
        );
      }

      return response.text();
    })
    .then((html) => {
      if (isConstructorCollectionLoaded(root)) {
        return false;
      }

      if (!importFallbackMarkup(root, html)) {
        throw new Error('Shopify collection fallback markup is missing.');
      }

      revealShopifyCollection(root, reason);
      return collectionModulesReady.then(() => {
        runFallbackCallbacks(root, reason);
        return true;
      });
    })
    .catch((error) => {
      if (isConstructorCollectionLoaded(root)) {
        return false;
      }

      revealShopifyCollection(root, reason);
      root
        .querySelector('[data-constructor-widget-results]')
        ?.setAttribute('hidden', '');
      root
        .querySelector('[data-constructor-widget-empty]')
        ?.removeAttribute('hidden');
      console.error('Constructor collection fallback error', error);

      return false;
    })
    .finally(() => {
      root.removeAttribute('data-constructor-collection-fallback-loading');
      pendingFallbacks.delete(root);
    });

  pendingFallbacks.set(root, request);

  return request;
};

export default loadShopifyCollectionFallback;
